/**
 * The only place `import.meta.env` is read. Section 16.
 *
 * Reading it anywhere else means a missing variable is discovered on a lab
 * phone rather than at start up. Each variable is named literally rather than
 * looked up by key, because Vite replaces `import.meta.env.VITE_X` at build
 * time by textual substitution and a dynamic key is not substituted at all.
 */

function orElse(value: string | undefined, fallback: string): string {
  return value === undefined || value === '' ? fallback : value;
}

export const env = {
  apiBaseUrl: orElse(import.meta.env.VITE_API_BASE_URL, '/api/v1'),
  /*
   * Defaults to FALSE, and that direction is the whole point.
   *
   * It used to default to true, so a build made without `.env.production` --
   * a fresh clone, a CI job with no env file, a hurried publish -- shipped an
   * application serving fabricated data out of an in-browser store. Nothing
   * about it looks wrong: cases save, lists fill, the screens all work. It is
   * only wrong when a second device shows a different set of cases, which is
   * how this was found.
   *
   * A missing variable now means the real API. A mock build has to ask for
   * itself, which is the safe way round: a developer who wanted mocks and did
   * not get them notices in seconds, and a lab that did not want them never
   * gets them at all.
   *
   * Written as a bare comparison rather than through `orElse`, which is not a
   * tidy-up. Vite replaces `import.meta.env.VITE_USE_MOCKS` with a literal at
   * build time, so this folds to `false` and Rollup drops the mock startup
   * branch in main.tsx along with the entire MSW chunk it imports. Through
   * `orElse` it stays a runtime call, the branch survives, and a real build
   * ships the mock server as dead weight sitting one bug away from running.
   *
   * The semantics are identical: `orElse(undefined, 'false') === 'true'` and
   * `undefined === 'true'` are both false, and so is the empty string.
   */
  useMocks: import.meta.env.VITE_USE_MOCKS === 'true',
  sentryDsn: orElse(import.meta.env.VITE_SENTRY_DSN, ''),
  appVersion: orElse(import.meta.env.VITE_APP_VERSION, '0.0.0-dev'),
  /**
   * The release this build is, and the day it was released: the newest entry of
   * src/shared/release/changelog.ts, baked in by vite.config.ts. The request of
   * 5 October 2026 (third), section 8.
   */
  appRelease: orElse(import.meta.env.VITE_APP_RELEASE, '0.0.0'),
  appReleaseDate: orElse(import.meta.env.VITE_APP_RELEASE_DATE, ''),
  isProduction: import.meta.env.PROD,
} as const;
